import { useMemo, useState } from "react";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiFetch } from "@/api/client";
import { getMyMailboxAccess } from "@/api/mailbox-access";
import type { MailboxResponse, TicketListRow } from "@/api/schemas";
import { FilterBar } from "@/components/inbox/filter-bar";
import { ListEmptyState } from "@/components/inbox/list-empty-state";
import { TicketRow } from "@/components/inbox/ticket-row";
import { FilterBarSkeleton, TicketListSkeleton } from "@/components/ui/skeleton";
import { useHotkey } from "@/hooks/use-hotkey";
import {
  applyTicketFilters,
  type SortKey,
  type StatusKey,
} from "@/lib/inbox-filters";
import { useCheatsheet } from "@/stores/cheatsheet";
import { useCommandPalette } from "@/stores/command-palette";

const inboxSearchSchema = z.object({
  filter: z.enum(["mine", "unassigned"]).optional(),
  status: z.enum(["open", "pending", "answered", "closed"]).optional(),
  view: z.string().optional(),
  sort: z.enum(["newest", "oldest", "sla"]).optional(),
  mailbox: z.string().uuid().optional(),
});

type InboxSearch = z.infer<typeof inboxSearchSchema>;

function InboxLayout() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const status: StatusKey = search.status ?? "all";
  const sort: SortKey = search.sort ?? "newest";
  const mailboxId = search.mailbox;

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["tickets", { mailbox: mailboxId ?? null }],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (mailboxId) qs.set("mailbox_id", mailboxId);
      const path = qs.size > 0 ? `/api/tickets?${qs.toString()}` : "/api/tickets";
      return apiFetch<TicketListRow[]>(path);
    },
  });
  const { data: myAccess } = useQuery({
    queryKey: ["mailbox-access", "me"],
    queryFn: getMyMailboxAccess,
  });
  const { data: mailboxes } = useQuery({
    queryKey: ["mailboxes", "for-filter"],
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
    staleTime: 5 * 60_000,
  });

  const filtered = useMemo(
    () => applyTicketFilters(tickets ?? [], { status, query, sort }),
    [tickets, status, query, sort],
  );

  useInboxHotkeys(filtered);

  function setSearch(next: Partial<InboxSearch>) {
    navigate({
      to: "/inbox",
      search: (prev: InboxSearch) => {
        const merged = { ...prev, ...next };
        if (merged.status === undefined || (next.status as string) === "all") {
          delete merged.status;
        }
        if (merged.sort === "newest") {
          delete merged.sort;
        }
        if (merged.mailbox === undefined) {
          delete merged.mailbox;
        }
        return merged;
      },
      replace: true,
    });
  }

  function onStatusChange(next: StatusKey) {
    setSearch({ status: next === "all" ? undefined : next });
  }
  function onSortChange(next: SortKey) {
    setSearch({ sort: next === "newest" ? undefined : next });
  }
  function onMailboxChange(next: string | null) {
    setSearch({ mailbox: next ?? undefined });
  }

  function clearFilters() {
    setQuery("");
    navigate({
      to: "/inbox",
      search: (prev) => ({ filter: prev.filter }),
      replace: true,
    });
  }

  return (
    <div className="flex h-full flex-col">
      {isLoading && !tickets ? (
        <FilterBarSkeleton />
      ) : (
        <FilterBar
          tickets={tickets ?? []}
          status={status}
          query={query}
          sort={sort}
          mailboxes={mailboxes ?? []}
          mailboxId={mailboxId}
          onStatusChange={onStatusChange}
          onQueryChange={setQuery}
          onSortChange={onSortChange}
          onMailboxChange={onMailboxChange}
        />
      )}
      <TicketListPane
        tickets={filtered}
        isLoading={isLoading}
        filter={search.filter}
        status={search.status}
        query={query}
        accessSummary={myAccess}
        onClearFilters={clearFilters}
      />
    </div>
  );
}

function TicketListPane({
  tickets,
  isLoading,
  filter,
  status,
  query,
  accessSummary,
  onClearFilters,
}: {
  tickets: TicketListRow[];
  isLoading: boolean;
  filter: string | undefined;
  status: string | undefined;
  query: string;
  accessSummary?: { mailbox_ids: string[]; role: string };
  onClearFilters: () => void;
}) {
  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto">
        <TicketListSkeleton />
      </div>
    );
  }

  if (!tickets.length) {
    return (
      <ListEmptyState
        filter={filter}
        status={status}
        query={query}
        accessSummary={accessSummary}
        onClearFilters={onClearFilters}
      />
    );
  }

  return (
    <ul className="flex-1 divide-y divide-border overflow-y-auto scrollbar-thin">
      {tickets.map((t) => (
        <TicketRow key={t.id} ticket={t} active={false} />
      ))}
    </ul>
  );
}

function useInboxHotkeys(tickets: TicketListRow[]) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const paletteOpen = useCommandPalette((s) => s.open);
  const cheatsheetOpen = useCheatsheet((s) => s.open);
  const ready = !paletteOpen && !cheatsheetOpen;

  function step(delta: 1 | -1) {
    const list =
      tickets.length > 0
        ? tickets
        : queryClient.getQueryData<TicketListRow[]>(["tickets"]) ?? [];
    if (list.length === 0) return;
    const nextIdx = delta === 1 ? 0 : list.length - 1;
    const next = list[nextIdx];
    if (!next) return;
    navigate({ to: "/tickets/$ticketId", params: { ticketId: next.id } });
  }

  useHotkey("j", () => step(1), { enabled: ready });
  useHotkey("k", () => step(-1), { enabled: ready });
}

export const Route = createFileRoute("/_auth/inbox")({
  validateSearch: inboxSearchSchema,
  component: InboxLayout,
});
